package com.evoapps.evoshift.domain.usecase.todo

import com.evoapps.evoshift.domain.model.CheckStatus
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffChecks
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.StaffRepository
import com.evoapps.evoshift.domain.usecase.documents.GetPoliciesUseCase
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/**
 * SH-170: the "Do zrobienia" of an employee at one workplace, live: their next shift when it is
 * near (with how long until it starts), availability the manager asked for and they have not sent
 * before the deadline, colleagues asking them to take a shift, their own rejected swap request to
 * dismiss, policies to confirm, their own medical exam or OHS training ending, and shifts on the
 * marketplace they could take. Only what the person may read already: their own shifts, the
 * published months' swaps (as the marketplace reads them), their own availability, the
 * workplace's policies and their own HR dates. Most urgent first ([TodoRules.sorted]).
 */
class ObserveMyTodoUseCase(
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository,
    private val availabilityRepository: AvailabilityRepository,
    private val getPolicies: GetPoliciesUseCase? = null,
    private val staffRepository: StaffRepository? = null,
    private val now: () -> Instant = { AppClock.now() },
    private val zone: () -> TimeZone = { TimeZone.currentSystemDefault() }
) {
    @OptIn(ExperimentalCoroutinesApi::class)
    operator fun invoke(userId: String, workplaceId: String): Flow<List<TodoItem>> {
        val nowLocal = now().toLocalDateTime(zone())
        val today = nowLocal.date
        val rosters = rosterRepository.observeRosters(workplaceId).catch { emit(emptyList()) }
        val mine = shiftRepository.observeShiftsForUser(userId, today.toString()).catch { emit(emptyList()) }
        return rosters.distinctUntilChanged().flatMapLatest { list ->
            val current = today.year * 12 + today.month.number - 1
            // Swaps are read per published month from this one on, as the marketplace reads them.
            val published = list.filter { it.isPublishedMonth() && it.year * 12 + it.month - 1 >= current }.map { it.id }
            val swaps: Flow<List<Shift>> = if (published.isEmpty()) flowOf(emptyList())
            else combine(published.map { id -> shiftRepository.observePendingSwaps(id).catch { emit(emptyList()) } }) {
                it.toList().flatten()
            }
            // The first list comes once every source answered.
            val slow = flow { emit(slowItems(userId, workplaceId, list, today)) }
            combine(mine, swaps, slow) { myShifts, pool, slower ->
                val items = buildList {
                    nextShiftItem(myShifts, workplaceId, nowLocal)?.let(::add)
                    rejectedItem(myShifts, userId, workplaceId)?.let(::add)
                    askedItem(pool, userId, today)?.let(::add)
                    marketplaceItem(pool, userId, today)?.let(::add)
                    addAll(slower)
                }
                TodoRules.sorted(items, today)
            }
        }.distinctUntilChanged()
    }

    private fun Roster.isPublishedMonth() = status == RosterStatus.PUBLISHED || status == RosterStatus.LOCKED

    private fun nextShiftItem(shifts: List<Shift>, workplaceId: String, now: LocalDateTime): TodoItem.NextShift? {
        val nowIso = now.toString()
        val next = shifts.filter { it.isPublished && RosterIds.workplaceIdOf(it.rosterId) == workplaceId && it.plannedTime.start > nowIso }
            .minByOrNull { it.plannedTime.start } ?: return null
        val start = runCatching { LocalDateTime.parse(next.plannedTime.start.take(19)) }.getOrNull() ?: return null
        val minutes = (start.toInstant(zone()) - now.toInstant(zone())).inWholeMinutes
        return if (minutes in 0..TodoRules.NEXT_SHIFT_HOURS * 60L) TodoItem.NextShift(next, minutes) else null
    }

    private fun rejectedItem(shifts: List<Shift>, userId: String, workplaceId: String): TodoItem.SwapRejected? =
        shifts.filter {
            RosterIds.workplaceIdOf(it.rosterId) == workplaceId &&
                it.swapRequest?.status == SwapStatus.REJECTED && it.swapRequest?.initiatorUserId == userId
        }.minByOrNull { it.date }?.let { TodoItem.SwapRejected(it.id, LocalDate.parse(it.date)) }

    private fun askedItem(pool: List<Shift>, userId: String, today: LocalDate): TodoItem.SwapsAsked? {
        val asked = pool.filter {
            it.swapRequest?.status == SwapStatus.REQUESTED_BY_OWNER && it.swapRequest?.candidateUserId == userId &&
                it.userId != userId && it.date >= today.toString()
        }.sortedBy { it.plannedTime.start }
        val first = asked.firstOrNull() ?: return null
        return TodoItem.SwapsAsked(asked.size, first.swapRequest?.initiatorName.orEmpty(), LocalDate.parse(first.date))
    }

    private fun marketplaceItem(pool: List<Shift>, userId: String, today: LocalDate): TodoItem.Marketplace? {
        val open = pool.filter { it.swapRequest?.status == SwapStatus.AVAILABLE && it.userId != userId && it.date >= today.toString() }
            .sortedBy { it.plannedTime.start }
        val first = open.firstOrNull() ?: return null
        return TodoItem.Marketplace(open.size, LocalDate.parse(first.date))
    }

    private suspend fun slowItems(userId: String, workplaceId: String, rosters: List<Roster>, today: LocalDate): List<TodoItem> = buildList {
        // Availability asked for and not sent, before its deadline (the months still being planned).
        rosters.filter { it.availabilityRequestId != null && (it.status == RosterStatus.DRAFT || it.status == RosterStatus.SKELETON) }
            .sortedBy { it.year * 100 + it.month }
            .forEach { roster ->
                val request = availabilityRepository.getAvailabilityRequest(roster.availabilityRequestId!!).getOrNull() ?: return@forEach
                val deadline = request.dueDate.take(10).let { runCatching { LocalDate.parse(it) }.getOrNull() }
                if (deadline != null && deadline < today) return@forEach
                val sent = availabilityRepository.findAvailability(userId, workplaceId, roster.getMonthYear()).getOrNull()?.isFilled == true
                if (!sent) add(TodoItem.AvailabilityDue(roster.id, roster.year, roster.month, deadline))
            }
        getPolicies?.invoke(workplaceId, userId)?.getOrNull()?.filterNot { it.isAcknowledged }?.takeIf { it.isNotEmpty() }?.let { open ->
            add(TodoItem.PoliciesToConfirm(workplaceId, open.size, open.first().policy.title))
        }
        staffRepository?.getStaffRecord(userId, workplaceId)?.getOrNull()?.let { record ->
            StaffCheck.entries.forEach { check ->
                val until = record.validUntil(check) ?: return@forEach
                when (StaffChecks.status(until, today)) {
                    CheckStatus.EXPIRED -> add(TodoItem.OwnCheck(check, until, expired = true))
                    CheckStatus.EXPIRING -> add(TodoItem.OwnCheck(check, until, expired = false))
                    else -> {}
                }
            }
        }
    }
}
